import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import './WikiItemList.css';
import { McIcon } from '../components/CraftingGrid';

const VIDEO = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4';

const RARITY_COLORS = {
  common: '#9e9e9e',
  uncommon: '#52c078',
  rare: '#5a9ee0',
  elite: '#a070e0',
  epic: '#c060d0',
  legendary: '#f0ab20',
  boss: '#e84c3d',
  unique: '#00e5c8',
};

const RARITY_GLOW = {
  common: 'rgba(154,144,128,0.12)',
  uncommon: 'rgba(82,192,120,0.12)',
  rare: 'rgba(90,158,224,0.12)',
  elite: 'rgba(160,112,224,0.12)',
  epic: 'rgba(192,96,208,0.12)',
  legendary: 'rgba(240,171,32,0.12)',
  boss: 'rgba(232,76,61,0.12)',
  unique: 'rgba(0,229,200,0.12)',
};

const RARITY_BORDER = {
  common: 'rgba(154,144,128,0.28)',
  uncommon: 'rgba(82,192,120,0.28)',
  rare: 'rgba(90,158,224,0.28)',
  elite: 'rgba(160,112,224,0.28)',
  epic: 'rgba(192,96,208,0.28)',
  legendary: 'rgba(240,171,32,0.28)',
  boss: 'rgba(232,76,61,0.28)',
  unique: 'rgba(0,229,200,0.28)',
};

const cat_icons = {
  All:        'book',
  Weapon:     'diamond_sword',
  Armour:     'diamond_chestplate',
  Tool:       'diamond_pickaxe',
  Utility:    'compass',
  Consumable: 'potion',
  Item:       'item_frame',
  Misc:       'chest',
};

const catorgories = ['All', 'Weapon', 'Armour', 'Tool', 'Utility', 'Consumable', 'Item', 'Misc'];

const RARITIES = ['all', 'legendary', 'boss', 'epic', 'rare', 'uncommon', 'common'];
const RARITY_RANK = { legendary: 6, boss: 5, epic: 4, rare: 3, uncommon: 2, common: 1 };

function itemMaterial(item) {
  return item?.material || item?.type || 'BARRIER';
}

export default function WikiItemList({ items = [], mobs = [] }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeRarity, setActiveRarity] = useState('all');
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').toLowerCase().trim();

  const enabledItems = items.filter(i => i.enabled !== 0 && i.enabled !== '0');

  let filtered = (activeCategory === 'All' ? enabledItems : enabledItems.filter(i => i.category === activeCategory));

  if (activeRarity !== 'all') filtered = filtered.filter(i => i.rarity === activeRarity);

  if (q) {
    filtered = filtered.filter(i =>
      i.name.toLowerCase().includes(q) ||
      i.type?.toLowerCase().includes(q) ||
      i.category?.toLowerCase().includes(q)
    );
  }

  filtered = [...filtered].sort((a, b) =>
    (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0)
  );

  return (
    <div className="wil-root">

      <div className="wil-hero">
        <video className="wil-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wil-hero-overlay" />
        <FadeIn className="wil-hero-content container" y={30} duration={0.8}>
          <div className="wil-hero-eyebrow">SimpleMC Wiki</div>
          <h1 className="wil-hero-title">Custom <em>Items</em></h1>
          <p className="wil-hero-sub">
            Weapons, armour, tools &amp; consumables — stats, crafting recipes, and special abilities.
          </p>
          <div className="wil-hero-stats">
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledItems.length}</span>
              <span className="wil-hero-stat-lbl">Total Items</span>
            </div>
            <div className="wil-hero-stat-div" />
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledItems.filter(i => i.rarity === 'legendary').length}</span>
              <span className="wil-hero-stat-lbl">Legendary</span>
            </div>
            <div className="wil-hero-stat-div" />
            <div className="wil-hero-stat">
              <span className="wil-hero-stat-num">{enabledItems.filter(i => i.category === 'Weapon').length}</span>
              <span className="wil-hero-stat-lbl">Weapons</span>
            </div>
          </div>
        </FadeIn>
      </div>

      <div className="container">

        {/* ── Filter bar ── */}
        <div className="wil-filter-bar">
          <div className="wil-filter-pills">
            {catorgories.map(cat => (
              <button
                key={cat}
                className={`wil-filter-btn${activeCategory === cat ? ' active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                <McIcon materialKey={cat_icons[cat] ?? 'barrier'} size={20} />
                {cat}
              </button>
            ))}
          </div>
          <div className="wil-filter-pills wil-rarity-pills">
            {RARITIES.map(r => (
              <button
                key={r}
                className={`wil-filter-btn wil-rarity-btn${activeRarity === r ? ' active' : ''}`}
                style={activeRarity === r && r !== 'all' ? { '--rarity-color': RARITY_COLORS[r], borderColor: RARITY_COLORS[r], color: RARITY_COLORS[r], background: `${RARITY_COLORS[r]}18` } : {}}
                onClick={() => setActiveRarity(r)}
              >
                {r === 'all' ? 'All rarities' : r}
              </button>
            ))}
          </div>
          <span className="wil-count">
            <strong>{filtered.length}</strong> {filtered.length === 1 ? 'item' : 'items'}
          </span>
        </div>

        {filtered.length === 0 ? (
          <div className="wil-empty">
            <McIcon materialKey="chest" size={48} />
            <p>No items found in this category yet.</p>
          </div>
        ) : (
          <StaggerContainer className="wil-grid">
            {filtered.map(item => {
              const rarityColor = RARITY_COLORS[item.rarity] || '#9e9e9e';
              const rarityGlow = RARITY_GLOW[item.rarity] || 'rgba(255,255,255,0.04)';
              const rarityBorder = RARITY_BORDER[item.rarity] || 'rgba(255,255,255,0.08)';
              return (
                <StaggerItem key={item.id} style={{ height: '100%' }}>
                  <Link
                    to={`/wiki/item/${item.id}`}
                    className="wil-card"
                    style={{
                      '--rarity-color': rarityColor,
                      '--rarity-glow': rarityGlow,
                      '--rarity-border': rarityBorder,
                    }}
                  >
                    {/* Rarity accent bar on top */}
                    <div className="wil-card-accent-bar" />

                    <div className="wil-card-top">
                      <div className="wil-card-icon-wrap">
                        <McIcon materialKey={itemMaterial(item)} size={32} />
                        <div className="wil-card-icon-glow" />
                      </div>
                      <div className="wil-card-title-block">
                        <div className="wil-card-name">{item.name}</div>
                        <div className="wil-card-type-row">
                          <span className="wil-rarity-badge">{item.rarity}</span>
                          <span className="wil-card-sep">·</span>
                          <span className="wil-card-type">{item.type}</span>
                        </div>
                      </div>
                    </div>

                    <div className="wil-card-desc">{item.description}</div>

                    {/* Abilities → lore → drop sources fallback */}
                    {(() => {
                      if (item.abilities?.length > 0) {
                        const shown = item.abilities.slice(0, 3);
                        const extra = item.abilities.length - shown.length;
                        return (
                          <div className="wil-card-abilities">
                            {shown.map((ab, i) => (
                              <div key={i} className="wil-card-ability-pill">
                                <span className="wil-card-ability-name">{ab.name}</span>
                                {ab.cooldown && (
                                  <span className="wil-card-ability-cd">{ab.cooldown}</span>
                                )}
                              </div>
                            ))}
                            {extra > 0 && (
                              <span className="wil-card-abilities-more">+{extra} more</span>
                            )}
                          </div>
                        );
                      }

                      // No abilities — try drop sources from obtain
                      let obtainEntries = [];
                      try { obtainEntries = JSON.parse(item.obtain || '[]'); } catch {}
                      const drops = obtainEntries.filter(e => e.type === 'drop' || e.type === 'block_drop' || e.type === 'supply_drop');
                      if (drops.length > 0) {
                        const shownDrops = drops.slice(0, 2);
                        const extraDrops = drops.length - shownDrops.length;
                        return (
                          <div className="wil-card-abilities">
                            <span className="wil-card-drops-label">Drop chances</span>
                            {shownDrops.map((e, i) => {
                              const mob = e.mob_id ? mobs.find(m => String(m.id) === String(e.mob_id)) : null;
                              const label = mob ? mob.name : e.mob_name || e.location || (e.type === 'supply_drop' ? 'Supply Drop' : null);
                              if (!label) return null;
                              return (
                                <div key={i} className="wil-card-ability-pill">
                                  <span className="wil-card-ability-name">{label}</span>
                                  {e.chance && <span className="wil-card-ability-cd">{e.chance}%</span>}
                                </div>
                              );
                            })}
                            {extraDrops > 0 && (
                              <span className="wil-card-abilities-more">+{extraDrops} more</span>
                            )}
                          </div>
                        );
                      }

                      // Fall back to lore
                      if (item.lore) {
                        const plain = item.lore.replace(/§[0-9a-frlonmkA-FRLONMK]/g, '').trim();
                        if (plain) return (
                          <div className="wil-card-lore">"{plain.length > 80 ? plain.slice(0, 80) + '…' : plain}"</div>
                        );
                      }

                      return null;
                    })()}

                    <div className="wil-card-footer">
                      <div className="wil-card-tags">
                        {item.tags?.slice(0, 3).map(tag => (
                          <span key={tag} className="wil-tag">{tag}</span>
                        ))}
                      </div>
                      <span className="wil-card-arrow">›</span>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        )}

      </div>
    </div>
  );
}
